import { last } from 'lodash';
import type { Dispatch, FC, SetStateAction } from 'react';
import { Fragment, useEffect, useState } from 'react';
import type { TestingData } from '@/api/api.v2';
import type { CommonPerfType, WellGeoData } from '@/store/geo-test/types';
import { STRATUM_BG, useMetaContext, WELL_COLUMN_WIDTH, WELL_WIDTH } from '../../base';
import type { GeoChartMeta } from '../../types';
import { getCoordinates, getRelatedCoordinates, getSelectedEntities, meanStratumsDiff, useGeoData } from '../../utils';
import { PERF_OFFSET, TEST_OFFSET } from '../GeoChart';
import { GeoLayerComponent, GeoLayerTooltip } from './GeoLayer';
import { PerforationComponent } from './Perforation';
import { TestingComponent } from './Testing';
import type { GeoChartBodyPictureProps, TooltipProps } from './types';

const getStratumsFigures = (
    well: WellGeoData,
    nextWell: WellGeoData,
    meta: GeoChartMeta,
    isFirstWell: boolean,
    isLastWell: boolean,
    setTooltipInfo: Dispatch<SetStateAction<TooltipProps>>,
) => {
    const foundedStratumKeys: number[] = [];
    const res: JSX.Element[] = [];

    for (const stratum of well.stratums) {
        let nextStratum = nextWell.stratums.find(({ key }) => key === stratum.key);
        let half = false;
        if (nextStratum) {
            foundedStratumKeys.push(stratum.key);
        } else {
            const rel = meanStratumsDiff(well, nextWell, stratum);
            nextStratum = {
                ...stratum,
                end: rel.bottom,
                start: rel.top,
            };
            half = true;
        }

        const {
            x: startX,
            bottom: startBottom,
            top: startTop,
        } = getCoordinates(well.x, stratum.end < 0 ? stratum.start : stratum.end, stratum.start, meta);
        const {
            x: endX,
            bottom: endBottom,
            top: endTop,
        } = getCoordinates(
            nextWell.x,
            nextStratum.end < 0 ? nextStratum.start : nextStratum.end,
            nextStratum.start,
            meta,
        );

        if (isFirstWell) {
            const additionalPath = `M ${0} ${startTop} L ${startX} ${startTop} L ${startX} ${startBottom} L ${0} ${startBottom} Z`;
            res.push(<path d={additionalPath} key={`additional-${stratum.key}`} fill={STRATUM_BG} />);
        }

        res.push(
            <GeoLayerComponent
                {...{
                    startX,
                    startTop,
                    startBottom,
                    endX,
                    endTop,
                    endBottom,
                    setTooltipInfo,
                    isLastWell,
                    half,
                }}
                geoObject={stratum}
            />,
        );
    }

    for (const stratum of nextWell.stratums) {
        if (foundedStratumKeys.includes(stratum.key)) {
            continue;
        }
        const rel = meanStratumsDiff(nextWell, well, stratum);
        const nextStratum = {
            ...stratum,
            end: rel.bottom,
            start: rel.top,
        };

        const {
            x: startX,
            bottom: startBottom,
            top: startTop,
        } = getCoordinates(nextWell.x, stratum.end, stratum.start, meta);

        const {
            x: endX,
            bottom: endBottom,
            top: endTop,
        } = getCoordinates(well.x, nextStratum.end, nextStratum.start, meta);

        res.push(
            <GeoLayerComponent
                {...{
                    startX,
                    startTop,
                    startBottom,
                    endX,
                    endTop,
                    endBottom,
                    setTooltipInfo,
                    isLastWell,
                    half: true,
                }}
                geoObject={stratum}
                nextGeoObject={nextStratum}
            />,
        );
    }
    return res;
};

const getSeamsFigures = (
    well: WellGeoData,
    nextWell: WellGeoData,
    meta: GeoChartMeta,
    setTooltipInfo: Dispatch<SetStateAction<TooltipProps>>,
    isLastWell: boolean,
) => {
    const nextSeams = nextWell.seams;
    const foundedKeys: string[] = [];
    const res: JSX.Element[] = [];
    const paintedKeys: string[] = [];

    for (const seam of well.seams) {
        const { top: relatedTop, bottom: relatedBottom } = getRelatedCoordinates(seam, well, nextWell, 'current');

        const relatedSeams = nextSeams.filter(
            nextSeam => Math.max(relatedTop, nextSeam.start) <= Math.min(relatedBottom, nextSeam.end),
        );

        const { x: startX, bottom: startBottom, top: startTop } = getCoordinates(well.x, seam.end, seam.start, meta);

        if (relatedSeams.length > 0) {
            relatedSeams.forEach(nextSeam => {
                const {
                    start,
                    end,
                    key,
                    saturant: { hex: colorEnd },
                } = nextSeam;
                foundedKeys.push(key);

                const { x: endX, bottom: endBottom, top: endTop } = getCoordinates(nextWell.x, end, start, meta);
                const seamKey = `${startX}${startTop}${startBottom}${endX}${endTop}${endBottom}`;
                if (paintedKeys.includes(seamKey)) {
                    return;
                }
                paintedKeys.push(seamKey);

                res.push(
                    <GeoLayerComponent
                        geoObject={seam}
                        nextGeoObject={nextSeam}
                        isLastWell={isLastWell}
                        setTooltipInfo={setTooltipInfo}
                        colorStart={seam.saturant.hex}
                        key={seamKey}
                        {...{
                            startX,
                            startTop,
                            startBottom,
                            endX,
                            endTop,
                            endBottom,
                            colorEnd,
                        }}
                    />,
                );
            });
        } else {
            const {
                x: endX,
                bottom: endBottom,
                top: endTop,
            } = getCoordinates(nextWell.x, relatedBottom, relatedTop, meta);
            const seamKey = `${startX}${startTop}${startBottom}${endX}${endTop}${endBottom}`;
            if (paintedKeys.includes(seamKey)) {
                continue;
            }
            paintedKeys.push(seamKey);

            res.push(
                <GeoLayerComponent
                    geoObject={seam}
                    setTooltipInfo={setTooltipInfo}
                    key={seamKey}
                    half
                    colorStart={seam.saturant.hex}
                    {...{ startX, startTop, startBottom, endX, endTop, endBottom }}
                />,
            );
        }
    }

    for (const seam of nextSeams) {
        if (foundedKeys.includes(seam.key)) {
            continue;
        }

        const { top: relatedTop, bottom: relatedBottom } = getRelatedCoordinates(seam, well, nextWell, 'next');

        const { x: endX, bottom: endBottom, top: endTop } = getCoordinates(well.x, relatedBottom, relatedTop, meta);
        const {
            x: startX,
            bottom: startBottom,
            top: startTop,
        } = getCoordinates(nextWell.x, seam.end, seam.start, meta);
        const seamKey = `${startX}${startTop}${startBottom}${endX}${endTop}${endBottom}`;
        if (paintedKeys.includes(seamKey)) {
            continue;
        }
        paintedKeys.push(seamKey);

        res.push(
            <GeoLayerComponent
                geoObject={seam}
                setTooltipInfo={setTooltipInfo}
                key={seamKey}
                isLastWell={isLastWell}
                half
                colorStart={seam.saturant.hex}
                {...{ startX, startTop, startBottom, endX, endTop, endBottom }}
            />,
        );
    }

    return res;
};

export type PerforationWithLevel = CommonPerfType & { level: number };
export type TestingWithLevel = TestingData & { level: number };

const intersects = (aTop: number, aBottom: number, bTop: number, bBottom: number): boolean => {
    return !(aBottom > bTop || aTop < bBottom);
};

export const addLevelsToTests = (testingData: TestingData[]): TestingWithLevel[] => {
    const tests = testingData
        .map((t, i) => ({ ...t, _idx: i }))
        .sort((a, b) => {
            if (a.absRoof !== b.absRoof) return a.absRoof - b.absRoof;
            return a.absBottom - a.absRoof - (b.absBottom - b.absRoof);
        });

    const assigned: TestingWithLevel[] = [];

    for (const t of tests) {
        const occupied: boolean[] = [];

        for (const prev of assigned) {
            if (
                intersects(
                    t.absRoof + TEST_OFFSET,
                    t.absBottom - TEST_OFFSET,
                    prev.absRoof + TEST_OFFSET,
                    prev.absBottom - TEST_OFFSET,
                )
            ) {
                occupied[prev.level] = true;
            }
        }

        let lvl = 0;
        while (occupied[lvl]) lvl++;
        assigned.push({ ...t, level: lvl });
    }

    const byIdx = new Array<TestingWithLevel>(testingData.length);
    for (const a of assigned) {
        byIdx[a._idx] = a;
        delete (a as any)._idx;
    }
    return byIdx;
};

export const addLevelsToPerforations = (
    perforationData: CommonPerfType[],
    testingData: TestingWithLevel[],
): PerforationWithLevel[] => {
    const withLevels: PerforationWithLevel[] = [];

    for (let i = 0; i < perforationData.length; i++) {
        const current = perforationData[i];

        let overlapCount = 0;
        for (let j = 0; j < i; j++) {
            const previous = withLevels[j];
            if (
                intersects(
                    current.absRoof + PERF_OFFSET,
                    current.absBottom - PERF_OFFSET,
                    previous.absRoof + PERF_OFFSET,
                    previous.absBottom - PERF_OFFSET,
                )
            ) {
                if (previous.level + 1 > overlapCount) {
                    overlapCount = previous.level + 1;
                }
            }
        }

        for (const test of testingData) {
            if (
                intersects(
                    current.absRoof + PERF_OFFSET,
                    current.absBottom - PERF_OFFSET,
                    test.absRoof + TEST_OFFSET,
                    test.absBottom - TEST_OFFSET,
                )
            ) {
                const candidate = test.level + 1;
                if (candidate > overlapCount) {
                    overlapCount = candidate;
                }
            }
        }

        const level = overlapCount;

        withLevels.push({
            ...current,
            level,
        });
    }

    return withLevels;
};

const getWellLines = (
    well: WellGeoData,
    meta: GeoChartMeta,
    fullHeight: number,
    testingByWells: Record<number, TestingData[]>,
    perforationData: CommonPerfType[],
) => {
    if (!meta.picture) {
        throw new Error('picture is missing in GeoChartBody');
    }

    const { depthToPixelsY } = meta.depths;
    const { distanceToPixelsX } = meta.picture;

    const x = distanceToPixelsX(well.x);
    const yBottom = depthToPixelsY(well.depth ?? last(well.stratums)?.end ?? fullHeight);
    const yTop = depthToPixelsY(0);

    const wellTesting = addLevelsToTests(testingByWells[well.wellId] ?? []);
    const perforationDataWithLevels = addLevelsToPerforations(perforationData, wellTesting);

    return (
        <Fragment key={x}>
            <line
                x1={x - WELL_COLUMN_WIDTH / 2}
                x2={x - WELL_COLUMN_WIDTH / 2}
                y1={0}
                y2={yBottom}
                stroke="currentColor"
            />
            {wellTesting &&
                wellTesting.map(testing => (
                    <TestingComponent key={testing.id} {...testing} x={x + WELL_COLUMN_WIDTH / 2} meta={meta} />
                ))}
            {perforationDataWithLevels.map(perforation => (
                <PerforationComponent key={perforation.id} {...perforation} x={x + WELL_COLUMN_WIDTH / 2} meta={meta} />
            ))}
            {WELL_COLUMN_WIDTH !== 0 && (
                <line
                    x1={x + WELL_COLUMN_WIDTH / 2}
                    x2={x + WELL_COLUMN_WIDTH / 2}
                    y1={yTop}
                    y2={yBottom}
                    stroke="currentColor"
                />
            )}
            {yBottom !== fullHeight && (
                <line x1={x - WELL_WIDTH / 2} x2={x + WELL_WIDTH / 2} y1={yBottom} y2={yBottom} stroke="currentColor" />
            )}
        </Fragment>
    );
};

const getScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
    let current = element;
    while (current) {
        const overflowY = getComputedStyle(current).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
            return current;
        }
        current = current.parentElement;
    }
    return null;
};

export const GeoChartBodyPicture: FC<GeoChartBodyPictureProps> = ({ width: fullWidth, height: fullHeight }) => {
    const meta = useMetaContext();
    const {
        testingData: testingByWells,
        geoData,
        perforationData,
        selectedPerforations,
        selectedTestings,
        svgBodyRef,
    } = useGeoData();
    const filteredPerforations = getSelectedEntities(perforationData, selectedPerforations);
    const filteredTesting = getSelectedEntities(testingByWells, selectedTestings);

    if (!meta.picture) {
        throw new Error('picture is missing in GeoChartBody');
    }

    const { depthToPixelsY } = meta.depths;

    const sortedWells = geoData?.wells.sort((a, b) => a.x - b.x);

    const seamFigures: JSX.Element[] = [];
    const stratumFigures: JSX.Element[] = [];
    const wellsLine: JSX.Element[] = [];

    const [tooltipInfo, setTooltipInfo] = useState<TooltipProps>({
        x: 0,
        y: 0,
        geoObject: null,
    });

    useEffect(() => {
        const scrollParent = getScrollableParent(svgBodyRef.current?.parentElement ?? null);
        if (!scrollParent) return;

        const handleScroll = () => {
            if (scrollParent.scrollTop > tooltipInfo.y) {
                setTooltipInfo(prev => ({ ...prev, y: scrollParent.scrollTop }));
            }
        };

        scrollParent.addEventListener('scroll', handleScroll);

        return () => {
            scrollParent.removeEventListener('scroll', handleScroll);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const setTooltipInfoWithScroll = (updater: TooltipProps | ((prev: TooltipProps) => TooltipProps)) => {
        const scrollParent = getScrollableParent(svgBodyRef.current?.parentElement ?? null);
        const scrollTop = scrollParent?.scrollTop ?? 0;

        setTooltipInfo(prev => {
            const nextState = typeof updater === 'function' ? updater(prev) : updater;

            return {
                ...nextState,
                y: Math.max(nextState.y, scrollTop),
            };
        });
    };

    let index = 0;

    for (const well of sortedWells ?? []) {
        if (!sortedWells) {
            break;
        }

        if (!sortedWells[index + 1]) {
            stratumFigures.push(
                ...getStratumsFigures(
                    well,
                    { ...well, x: well.x + 2 * meta.picture.endX },
                    meta,
                    false,
                    false,
                    setTooltipInfoWithScroll,
                ),
            );
            seamFigures.push(
                ...getSeamsFigures(
                    well,
                    { ...well, x: well.x + 2 * meta.picture.endX },
                    meta,
                    setTooltipInfoWithScroll,
                    false,
                ),
            );
            break;
        }

        const isLastWell = !sortedWells[index + 2];
        const isFirstWell = index === 0;
        const nextWell = sortedWells[index + 1];

        stratumFigures.push(
            ...getStratumsFigures(well, nextWell, meta, isFirstWell, isLastWell, setTooltipInfoWithScroll),
        );
        seamFigures.push(...getSeamsFigures(well, nextWell, meta, setTooltipInfoWithScroll, isLastWell));

        wellsLine.push(getWellLines(well, meta, fullHeight, filteredTesting, filteredPerforations[well.wellId] ?? []));
        if (isLastWell) {
            wellsLine.push(
                getWellLines(nextWell, meta, fullHeight, filteredTesting, filteredPerforations[nextWell.wellId] ?? []),
            );
        }

        index++;
    }

    return (
        <>
            <GeoLayerTooltip {...tooltipInfo} />
            <svg width={fullWidth} height={fullHeight} ref={svgBodyRef}>
                {stratumFigures}
                {seamFigures}
                {wellsLine}
                {geoData.horizons.some(h => (h.depth ?? 0) > 0) &&
                    geoData.horizons.map((horizon, index) => (
                        <g key={`horizon-${index}`}>
                            <line
                                x1={0}
                                x2={(sortedWells ?? []).length * 400}
                                y1={depthToPixelsY(horizon.depth ?? 0)}
                                y2={depthToPixelsY(horizon.depth ?? 0)}
                                stroke={horizon.lineColor}
                                strokeWidth={horizon.lineWidth}
                            />
                            <text
                                x={(sortedWells ?? []).length * 50}
                                y={depthToPixelsY((horizon.depth ?? 0) + 3)}
                                fill={'currentColor'}
                                fontSize={14}
                            >
                                {`${horizon.name ?? ''} ${horizon.depth} м`}
                            </text>
                        </g>
                    ))}
            </svg>
        </>
    );
};
